import React from 'react';

import type { Text } from '@zillow/constellation';
import { AdornedInput, Adornment, FormField, Input, Label, Spacer } from '@zillow/constellation';
import { CurrencyInput } from '@zillow/zrs-comet';

import type { Section } from '../../../../models/index';
import type { HTMLElementEvent } from '../../../../models/Types';
import { HEATING_OIL } from '../../../../constants';
import HeaderToggle from '../Utilities/HeaderToggle';
import { CenteredHR } from '../../../Widgets/CenteredHR';
import SwitchWithTitle from '../../../Widgets/SwitchWithTitle';
import { CURRENCY_INPUT_PLACEHOLDER, UTILITIES_HEADING_AND_CONTENT } from '../../../../constants/stringConstants';
import RadioListBox from '../../../Widgets/RadioListBox';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
  additionalFields?: object;
};

const INITIAL_STATE = {
  [HEATING_OIL.FIELD_NAME.IS_ENABLED]: true,
  [HEATING_OIL.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT]: false,
  [HEATING_OIL.FIELD_NAME.SERVICE_PROVIDER_TENANT_RESPONSIBILITY]: '',
  [HEATING_OIL.FIELD_NAME.FIXED_AMOUNT_VALUE]: null,
  [HEATING_OIL.FIELD_NAME.LANDLORD_RESPONSIBILITY]: false,
  [HEATING_OIL.FIELD_NAME.TENANT_RESPONSIBILITY]: false,
};

function HeatingOil_v3({ additionalFields, section, setSectionValues }: Props) {
  const heatingOil_isEnabled = section.getFieldValue(HEATING_OIL.FIELD_NAME.IS_ENABLED);
  const heatingOil_paysInAdditionIsToggled = section.getFieldValue(
    HEATING_OIL.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT,
  );
  const tenantSelected = section.getFieldValue(HEATING_OIL.FIELD_NAME.TENANT_RESPONSIBILITY);
  const landlordSelected = section.getFieldValue(HEATING_OIL.FIELD_NAME.LANDLORD_RESPONSIBILITY);
  const fixedAmountValue = section.getFieldValue(HEATING_OIL.FIELD_NAME.FIXED_AMOUNT_VALUE);

  function toggleUtility(): void {
    setSectionValues({
      ...INITIAL_STATE,
      [HEATING_OIL.FIELD_NAME.IS_ENABLED]: !section.getFieldValue(HEATING_OIL.FIELD_NAME.IS_ENABLED),
      ...additionalFields,
    });
  }

  function togglePaysInAddition(): void {
    setSectionValues({
      ...INITIAL_STATE,
      [HEATING_OIL.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT]: !section.getFieldValue(
        HEATING_OIL.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT,
      ),
      ...additionalFields,
    });
  }

  function setServiceProvider(event: HTMLElementEvent<typeof Text>): void {
    setSectionValues({
      [HEATING_OIL.FIELD_NAME.SERVICE_PROVIDER_TENANT_RESPONSIBILITY]: event.currentTarget.value,
    });
  }

  function setFixedAmountValue(event: HTMLElementEvent<typeof Text>): void {
    setSectionValues({
      [HEATING_OIL.FIELD_NAME.FIXED_AMOUNT_VALUE]: event.currentTarget.value,
    });
  }

  return (
    <>
      <HeaderToggle
        header="Heating oil"
        subheader="Applies to this property"
        switchOn={heatingOil_isEnabled}
        onSwitchToggle={toggleUtility}
      />
      {heatingOil_isEnabled && (
        <>
          <CenteredHR />
          <SwitchWithTitle
            text="Tenant pays in addition to base rent"
            onClick={togglePaysInAddition}
            toggled={heatingOil_paysInAdditionIsToggled}
          />
          {heatingOil_paysInAdditionIsToggled && (
            <Spacer marginTop="sm">
              <FormField
                label={<Label>Who will be responsible for heating oil set up?</Label>}
                control={
                  <RadioListBox
                    section={section}
                    setSectionValues={setSectionValues}
                    option1FieldName={HEATING_OIL.FIELD_NAME.TENANT_RESPONSIBILITY}
                    option2FieldName={HEATING_OIL.FIELD_NAME.LANDLORD_RESPONSIBILITY}
                    option1Header={UTILITIES_HEADING_AND_CONTENT.option1Heading}
                    option1Content={UTILITIES_HEADING_AND_CONTENT.option1Content}
                    option2Header={UTILITIES_HEADING_AND_CONTENT.option2Heading}
                    option2Content={UTILITIES_HEADING_AND_CONTENT.option2Content}
                  />
                }
              />
              {tenantSelected && (
                <FormField
                  marginTop="sm"
                  label={<Label>What service provider?</Label>}
                  control={
                    <Input
                      placeholder="(example: Holly Heating Oil Co.)"
                      value={section.getFieldValue(HEATING_OIL.FIELD_NAME.SERVICE_PROVIDER_TENANT_RESPONSIBILITY)}
                      onChange={setServiceProvider}
                    />
                  }
                />
              )}
              {landlordSelected && (
                <FormField
                  marginTop="sm"
                  label={<Label>What is the additional fixed fee for heating oil?</Label>}
                  control={
                    <AdornedInput
                      inputId="AdornedInput-fixed-amount-value"
                      leftAdornment={<Adornment>$</Adornment>}
                      rightAdornment={<Adornment>per month</Adornment>}
                      input={
                        <CurrencyInput
                          aria-label="fixed amount"
                          data-test="fixedAmountValue"
                          placeholder={CURRENCY_INPUT_PLACEHOLDER}
                          value={fixedAmountValue || ''}
                          onBlur={setFixedAmountValue}
                          onChange={setFixedAmountValue}
                        />
                      }
                    />
                  }
                />
              )}
            </Spacer>
          )}
        </>
      )}
    </>
  );
}

HeatingOil_v3.description = 'Heating Oil with fixed fee and option selector for tenant/landlord responsibility';

export default HeatingOil_v3;
